import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useDreamTracking } from '@xing-com/crate-dream-machine-tracking';
import { Dialog } from '@xing-com/dialog';
import { IconTick, IconPlus } from '@xing-com/icons';

import { useDeckCardTyped } from '../../context';

import { PeakAndPop, type PeakAndPopType } from './peak-and-pop';
import Styles from './peak-and-pop-skills.style';
import { useSkills } from './use-skills';

export const PeakAndPopSkills: React.FC<PeakAndPopType> = ({ ...props }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const { data } = useDeckCardTyped(['TakenJob', 'AvailableJob'] as const);
  const { addSkills } = useSkills();
  const { trackSkillsAddToProfile, trackSkillsAddWithDialog } =
    useDreamTracking();
  const intl = useIntl();

  useEffect(() => {
    if (!props.isShown && selectedSkills.length > 0) {
      setShowDialog(true);
    }
  }, [props.isShown]);

  if (!data?.skillsTotal) {
    return null;
  }

  const toggleSelection = (skill: string) => () => {
    if (selectedSkills.includes(skill)) {
      setSelectedSkills(
        selectedSkills.filter((selectedSkill) => selectedSkill !== skill)
      );
    } else {
      setSelectedSkills([...selectedSkills, skill]);
    }
  };

  const handleAddSkills = (): void => {
    addSkills(selectedSkills);
    props.setIsShown(false);
    setSelectedSkills([]);
  };

  const handleCancelDialog = (): void => {
    setShowDialog(false);
    setSelectedSkills([]);
  };

  const handleConfirmDialog = (): void => {
    setShowDialog(false);
    handleAddSkills();
    trackSkillsAddWithDialog(
      {
        status: data.type,
        cardType: 'job_reco',
        itemUrn: data.urn || undefined,
        trackingToken: data.trackingToken || undefined,
      },
      selectedSkills.length
    );
  };

  const handleAddToProfileClick = (): void => {
    handleAddSkills();
    trackSkillsAddToProfile(
      {
        status: data.type,
        cardType: 'job_reco',
        itemUrn: data.urn || undefined,
        trackingToken: data.trackingToken || undefined,
      },
      selectedSkills.length
    );
  };

  return (
    <>
      <PeakAndPop {...props}>
        <Styles.Illustration size="small" />
        <Styles.ScrollableContainer>
          {(data.skillsMatched?.length || 0) > 0 && (
            <Styles.Title size="medium">
              <FormattedMessage id="DREAM_MACHINE_JOB_CARD_SKILLS_TILE_TITLE_MATCH" />
            </Styles.Title>
          )}
          {data.skillsMatched &&
            data.skillsMatched.map((skillMatched) => (
              <Styles.Tag
                key={skillMatched}
                leadingIcon={IconTick}
                disabled={true}
                onClick={() => {
                  return null;
                }}
                maxWidth="200px"
                data-qa="dm-skill-matched"
              >
                {skillMatched}
              </Styles.Tag>
            ))}
          {data.skillsNotMatched?.length && (
            <Styles.Title size="medium">
              <FormattedMessage id="DREAM_MACHINE_JOB_CARD_SKILLS_TILE_TITLE_NOT_MATCH" />
            </Styles.Title>
          )}
          {data.skillsNotMatched &&
            data.skillsNotMatched.map((skillNotMatched) => {
              const isSelected =
                (skillNotMatched && selectedSkills.includes(skillNotMatched)) ||
                false;
              return (
                skillNotMatched && (
                  <Styles.Tag
                    key={skillNotMatched}
                    leadingIcon={isSelected ? IconTick : IconPlus}
                    selected={isSelected}
                    onClick={toggleSelection(skillNotMatched)}
                    maxWidth="200px"
                    data-qa="dm-skill-not-matched"
                  >
                    {skillNotMatched}
                  </Styles.Tag>
                )
              );
            })}
        </Styles.ScrollableContainer>
        <Styles.Note size="small">
          <FormattedMessage id="DREAM_MACHINE_JOB_CARD_SKILLS_PEEK_NOTE" />
        </Styles.Note>
        <Styles.AddButton
          variant="primary"
          size="medium"
          disabled={selectedSkills.length === 0}
          onClick={handleAddToProfileClick}
          data-qa="dm-skill-add-button"
        >
          <FormattedMessage id="DREAM_MACHINE_JOB_CARD_SKILLS_PEEK_ADD_CTA" />
        </Styles.AddButton>
      </PeakAndPop>
      <Dialog
        cancelLabel={intl.formatMessage({
          id: 'DREAM_MACHINE_JOB_CARD_SKILLS_ALERT_CONFIRMATION_DIALOG_CANCEL',
        })}
        confirmLabel={intl.formatMessage({
          id: 'DREAM_MACHINE_JOB_CARD_SKILLS_ALERT_CONFIRMATION_DIALOG_ADD',
        })}
        loading={false}
        onCancel={handleCancelDialog}
        onConfirm={handleConfirmDialog}
        show={showDialog}
        text={intl.formatMessage({
          id: 'DREAM_MACHINE_JOB_CARD_SKILLS_ALERT_CONFIRMATION_DIALOG_TITLE',
        })}
      />
    </>
  );
};
